import {
  EuiFlexGroup,
  EuiFlexItem,
  EuiText,
  EuiForm,
  EuiButton,
  EuiPanel,
} from "@inscopix/ideas-eui";
import { useState } from "react";
import { addUtilityToastFailure } from "utils/addUtilityToastFailure";
import { addUtilityToastSuccess } from "utils/addUtilityToastSuccess";
import { FieldTableKey } from "pages/gdt/modals/fields/FieldTableKey";
import { FieldTableName } from "pages/gdt/modals/fields/FieldTableName";
import { useDataTableContext } from "pages/gdt/store/DataTableProvider";

type Fields = {
  name: {
    value: string;
    isValid: boolean;
  };
  key: {
    value: string;
    isValid: boolean;
    isDirty: boolean;
  };
};

/*
 * Project page body panel to create the first data table in a project
 */
export const PanelCreateFirstTable = () => {
  const setSelectedTableId = useDataTableContext((s) => s.setSelectedTableId);
  const createDataTable = useDataTableContext((s) => s.createDataTable);
  const [isLoading, setIsLoading] = useState(false);
  const [fields, setFields] = useState<Fields>({
    name: {
      value: "",
      isValid: false,
    },
    key: {
      value: "",
      isValid: false,
      isDirty: false,
    },
  });

  const handleFieldChange = <T extends keyof Fields>(
    fieldName: T,
    field: Fields[T],
  ) => {
    setFields((prevFields) => ({
      ...prevFields,
      [fieldName]: field,
    }));
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    const { data, error } = await createDataTable({
      name: fields.name.value,
      key: fields.key.value,
      folder: null,
    });
    setIsLoading(false);

    if (error === undefined) {
      addUtilityToastSuccess("Data table created");
    } else {
      addUtilityToastFailure("Failed to create data table");
    }
    if (data != null) {
      setSelectedTableId(data);
    }
  };

  return (
    <EuiPanel paddingSize="l" hasShadow={false} hasBorder={true}>
      <EuiFlexGroup direction={"column"} gutterSize={"s"}>
        <EuiFlexGroup alignItems={"center"}>
          <EuiFlexItem>
            <EuiText>
              <h3>Create your first data table</h3>
            </EuiText>
          </EuiFlexItem>
          <EuiFlexItem grow={false}></EuiFlexItem>
        </EuiFlexGroup>
        <EuiFlexItem>
          <EuiText>
            <p>
              Use data tables to store and organize files, metadata, and
              analysis results.
            </p>
          </EuiText>
        </EuiFlexItem>
        <EuiFlexItem>
          <EuiForm component="form">
            <FieldTableName
              autoFocus
              value={fields.name.value}
              onChange={(field) => handleFieldChange("name", field)}
            />
            <FieldTableKey
              tableName={fields.name.value}
              value={fields.key.value}
              onChange={(field) => {
                handleFieldChange("key", { ...field, isDirty: true });
              }}
              autoGenerate={!fields.key.isDirty}
              onAutoGenerate={(key) => {
                handleFieldChange("key", {
                  value: key,
                  isValid: true,
                  isDirty: false,
                });
              }}
            />
            <EuiButton
              isLoading={isLoading}
              fill
              onClick={() => void handleSubmit()}
              disabled={Object.values(fields).some((field) => !field.isValid)}
            >
              Create
            </EuiButton>
          </EuiForm>
        </EuiFlexItem>
      </EuiFlexGroup>
    </EuiPanel>
  );
};
